import { useStyletron } from 'baseui';
import { Alert } from 'baseui/icon';
import { MaskedInput, MaskedInputProps } from 'baseui/input';
import { colors } from 'baseui/tokens';
import { toSentenceCase, toTitleCase } from 'common/utils/string';
import * as React from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import CustomFormControl, {
  CustomFormControlProps,
} from '../custom-form-control';
import { BaseElementInputProps } from '../field-type';
import { FormContext } from '../form';
import ReadOnlyText from '../read-only-text';
import { styles } from '../styles';
import Text from '../text';

export const FontCase = {
  Default: 'Default',
  TitleCase: 'TitleCase',
  SentenceCase: 'SentenceCase',
} as const;

export type FontCaseType = (typeof FontCase)[keyof typeof FontCase];

export interface TextInputProps
  extends BaseElementInputProps,
    Omit<
      CustomFormControlProps,
      'name' | 'overrides' | 'positive' | 'error' | 'children'
    >,
    Omit<MaskedInputProps, 'name'> {
  type: 'text' | 'password' | 'email' | 'tel';
  hint?: string;
  fieldType?: 'control' | 'uncontrol';
  fontCase?: FontCaseType;
  onAfterChange?: (value: any) => void;
}

function Negative() {
  const [css, theme] = useStyletron();

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        paddingRight: theme.sizing.scale500,
        color: theme.colors.negative400,
      })}
    >
      <Alert size="18px" />
    </div>
  );
}

export default function TextField(props: TextInputProps) {
  const { control } = useFormContext<any>();
  const watchValue = useWatch({ control, name: props.name });
  const {
    field: { ref, ...field },
    fieldState,
  } = useController({ name: props.name });
  const context = React.useContext(FormContext);

  const {
    readOnly,
    overrides,
    label = '',
    hint,
    disabled: _disabled,
    fieldType = 'control',
    clearable = true,
    fontCase = FontCase.Default,
    onAfterChange,
    ...restProps
  } = props;

  const shouldShowError = !!fieldState.error?.message;

  const disabled = _disabled || !context.editable;

  const textTransformer = React.useCallback(
    (text: string) => {
      switch (fontCase) {
        case 'TitleCase':
          return toTitleCase(text);
        case 'SentenceCase':
          return toSentenceCase(text);
        case 'Default':
        default:
          return text;
      }
    },
    [fontCase],
  );

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const result = textTransformer(e.target.value);
      field.onChange(result);
      onAfterChange?.(result);
    },
    [field, onAfterChange, textTransformer],
  );

  if (readOnly) {
    return (
      <ReadOnlyText
        label={label}
        value={
          props.type === 'password'
            ? watchValue
              ? Array(watchValue.length + 1).join('•')
              : ''
            : watchValue
        }
      />
    );
  }

  return (
    <CustomFormControl
      {...restProps}
      label={label}
      disabled={disabled}
      error={fieldState.error?.message}
    >
      <>
        <MaskedInput
          error={shouldShowError}
          /** tampaknya inputRef ada conflict dengan ref rhf */
          // inputRef={ref}
          {...field}
          {...props}
          {...(fieldType === 'uncontrol' && {
            value: watchValue,
          })}
          disabled={disabled}
          overrides={{
            Root: { style: { ...(disabled ? styles.reset : styles.active) } },
            Input: {
              style: {
                paddingTop: '2px',
                paddingBottom: '2px',
                ...(disabled ? styles.reset : styles.active),
              },
            },
            StartEnhancer: {
              style: {
                backgroundColor: 'transparent',
              },
            },
            InputContainer: {
              style: { ...(disabled ? styles.reset : styles.active) },
            },
            ...props.overrides,
            ...(shouldShowError ? { After: Negative } : {}),
          }}
          clearable={clearable}
          onChange={onChange}
        />
        {!!hint && (
          <Text
            variant="body2Regular"
            $style={{ marginTop: '8px', color: colors.gray400 }}
          >
            {hint}
          </Text>
        )}
      </>
    </CustomFormControl>
  );
}
